import React, { FC } from 'react'

import { isEmpty } from 'lodash'
import { Control, Controller, FieldValues } from 'react-hook-form'
import { ANNOUNCEMENT_NAMES, ANNOUNCEMENT_FIELD, FORM } from 'config'

import { ModalBoxHeaderWrapper, TextWrapper } from 'components/NotificationPanel/style'
import hookForms from 'utils/hookForms'
import { AppMenu } from 'components/Common/AppMenu/AppMenu.component'
import { AppCheckBox } from 'components/Common/AppFormFields/AppCheckBox/AppCheckBox.component'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { AppSendMessageIcon } from 'components/Common/AppSvgIcons/AppSendMessageIcon'
import { AppTextArea } from 'components/Common/AppFormFields/AppTextArea/AppTextArea.component'
import { AppFileUploader } from 'components/AppFileUploader/AppFileUploader.component'
import { UserFile } from '__generated__/api-types-and-hooks'
import { AppFileCard } from 'components/Common/AppFileCard/AppFileCard.component'

interface BoOption {
  label: string
  value: string
  initials?: string
}
interface IPublishAnnouncement {
  options: BoOption[]
  control: Control<FieldValues>
  isFetchingResponse: boolean
  onSubmit: () => void
  isLoading: boolean
  onUploadCompleted: (uploaded: UserFile[] | null) => void
  uploadedFiles?: UserFile[] | null
  onRemoveUploadedFile?: () => void
}

const PublishAnnouncement: FC<IPublishAnnouncement> = ({
  control,
  onSubmit,
  options,
  isFetchingResponse,
  isLoading,
  onUploadCompleted,
  uploadedFiles,
  onRemoveUploadedFile,
}) => {
  const formValues = hookForms.getForm(FORM.ANNOUNCEMENT_FORM)?.getValues()

  return (
    <>
      <ModalBoxHeaderWrapper>
        <TextWrapper>To:</TextWrapper>
        <div className="pr-6 w-full mb-6">
          <Controller
            name={ANNOUNCEMENT_FIELD.RECEIVER_IDS}
            control={control}
            defaultValue={[]}
            render={({ field }) => (
              <AppMenu
                options={options}
                placeholder={'All My Clients'}
                {...field}
                isSelectedAll={true}
              />
            )}
          />
        </div>
      </ModalBoxHeaderWrapper>
      <div className="border border-app-grey-20 " />
      <div className="px-6 py-6">
        <Controller
          name={ANNOUNCEMENT_FIELD.TEXT}
          control={control}
          render={({ field }) => (
            <AppTextArea
              rows={8}
              {...field}
              disabled={isFetchingResponse}
              placeholder={ANNOUNCEMENT_NAMES.PLACEHOLDER}
            />
          )}
        />
      </div>
      <div className="border border-app-grey-20 " />
      <div className="px-6 py-6">
        <div>
          <div className="text-sm font-medium mb-2">Upload Files</div>
          <AppFileUploader
            maxFiles={1}
            onUploadCompleted={onUploadCompleted}
            isFilesSaving={false}
          />
          {uploadedFiles && uploadedFiles.length > 0 && (
            <div className="mt-3">
              <AppFileCard file={uploadedFiles[0]} onRemove={onRemoveUploadedFile} />
            </div>
          )}
        </div>
      </div>
      <div className="border border-app-grey-20 " />
      <div className=" flex flex-col gap-2 px-6 py-7 mt-3">
        <Controller
          name={ANNOUNCEMENT_FIELD.SEND_TO_EMAIL}
          control={control}
          defaultValue={true}
          render={({ field }) => (
            <AppCheckBox
              id={ANNOUNCEMENT_FIELD.SEND_TO_EMAIL}
              label={'Notify via email'}
              {...field}
            />
          )}
        />

        <Controller
          name={ANNOUNCEMENT_FIELD.SEND_TO_SMS}
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <AppCheckBox
              id={ANNOUNCEMENT_FIELD.SEND_TO_SMS}
              label={'Notify via text'}
              {...field}
              toolTipText={'For clients that have opted in to SMS messaging'}
            />
          )}
        />
      </div>
      <div className="border border-app-grey-20 " />
      <div className="flex justify-end px-8 py-7 bg-app-grey-10">
        <AppButton
          label={'Send'}
          disabled={(isEmpty(formValues[ANNOUNCEMENT_FIELD.TEXT]) ? true : false) || isLoading}
          onClick={() => onSubmit()}
          size={'lg'}
          variant={'primary'}
          RightIcon={AppSendMessageIcon}
        />
      </div>
    </>
  )
}

export default PublishAnnouncement
